<script setup lang="ts">
import type { DocumentItem } from '@/js/account/entities/document';
import type {
  LibraryFiltersModel,
  LibraryListResponse,
  LibrarySortField,
} from '@/js/account/entities/document/model/types';
import { computed, ref } from 'vue';
import BaseLoadState from '@/js/account/components/base/BaseLoadState.vue';
import BasePagination from '@/js/account/components/base/BasePagination.vue';
import DocumentCard from '@/js/account/components/document/DocumentCard.vue';
import LibraryFilters from '@/js/account/components/library/LibraryFilters.vue';
import LibrarySearchResults from '@/js/account/components/library/search/LibrarySearchResults.vue';
import SortSelect from '@/js/account/components/library/SortSelect.vue';
import ConfirmDialog from '@/js/account/components/modals/ConfirmDialog.vue';
import SearchBar from '@/js/account/components/search/SearchBar.vue';
import { LIBRARY_SORT_OPTIONS, useDocumentDownload } from '@/js/account/entities/document';
import { accountDocumentsDestroy } from '@/js/account/entities/document/api';
import { useAuth } from '@/js/account/entities/session';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    list: LibraryListResponse | null
    loading: boolean
    error: string | null
    perPage: number
    emptyMessage: string
    enableSearchResults?: boolean
  }>(),
  { enableSearchResults: false },
);

const emit = defineEmits<{
  'reload-from-first-page': []
  'page-change': [page: number]
  'reload': []
}>();

const search = defineModel<string>('search', { required: true });
const sortBy = defineModel<LibrarySortField>('sortBy', { required: true });
const filters = defineModel<LibraryFiltersModel>('filters', { required: true });
const page = defineModel<number>('page', { required: true });

const cn = classNames('library-documents-browse');

const { downloadingId, onDocumentDownload } = useDocumentDownload();
const { isAdmin } = useAuth();

const searchList = computed(() =>
  props.enableSearchResults && props.list?.search ? props.list : null,
);

const documentToDelete = ref<DocumentItem | null>(null);
const deleteDialogOpen = ref(false);
const deletingId = ref<number | null>(null);
const deleteError = ref<string | null>(null);

function openDeleteDialog(document: DocumentItem): void {
  documentToDelete.value = document;
  deleteError.value = null;
  deleteDialogOpen.value = true;
}

function closeDeleteDialog(): void {
  deleteDialogOpen.value = false;
  documentToDelete.value = null;
  deleteError.value = null;
}

async function onConfirmDelete(): Promise<void> {
  if (!documentToDelete.value) {
    return;
  }

  const documentId = documentToDelete.value.id;
  deletingId.value = documentId;
  deleteError.value = null;

  try {
    await accountDocumentsDestroy(documentId);
    closeDeleteDialog();
    emit('reload');
  }
  catch {
    deleteError.value = 'Failed to delete document. Please try again.';
  }
  finally {
    deletingId.value = null;
  }
}
</script>

<template>
  <div :class="cn()">
    <SearchBar
      v-model="search"
      @search="emit('reload-from-first-page')"
    />
    <div :class="cn('layout', { wide: searchList !== null })">
      <div :class="cn('sidebar')">
        <SortSelect
          v-model="sortBy"
          :options="LIBRARY_SORT_OPTIONS"
          @update:model-value="emit('reload-from-first-page')"
        />
        <LibraryFilters
          v-model="filters"
          :loading="loading"
          @apply="emit('reload-from-first-page')"
          @clean="emit('reload-from-first-page')"
        />
      </div>
      <BaseLoadState
        :class="cn('results')"
        :loading="loading"
        :error="error"
        :empty="list !== null && list.total === 0"
        :empty-message="emptyMessage"
      >
        <LibrarySearchResults
          v-if="searchList"
          :list="searchList"
          :downloading-id="downloadingId"
          :deleting-id="deletingId"
          :show-admin-actions="isAdmin"
          @download="onDocumentDownload"
          @delete="openDeleteDialog"
        />
        <template v-else>
          <div :class="cn('results-list')">
            <DocumentCard
              v-for="document in list?.items ?? []"
              :key="document.id"
              :document="document"
              :downloading="downloadingId === document.id"
              :deleting="deletingId === document.id"
              :show-admin-actions="isAdmin"
              @download="onDocumentDownload(document, $event)"
              @delete="openDeleteDialog(document)"
            />
          </div>
          <BasePagination
            v-if="list"
            :page="page"
            :total="list.total"
            :per-page="perPage"
            :disabled="loading"
            :class="cn('pagination')"
            @update:page="emit('page-change', $event)"
          />
        </template>
      </BaseLoadState>
    </div>

    <ConfirmDialog
      v-model:open="deleteDialogOpen"
      title="Confirm Deletion"
      message="This policy will be permanently removed from the Wisdom Center. Are you sure you want to continue?"
      :loading="deletingId !== null"
      @confirm="onConfirmDelete"
      @cancel="closeDeleteDialog"
    />
    <p v-if="deleteError" :class="cn('delete-error')" role="alert">
      {{ deleteError }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.library-documents-browse {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__layout {
    display: grid;
    grid-template-columns: 279px minmax(300px, 530px);
    gap: 16px;

    &_wide {
      grid-template-columns: 279px minmax(300px, 1fr);
    }
  }

  &__sidebar {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__results {
    min-height: 200px;
  }

  &__results-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 24px;
  }

  &__delete-error {
    margin: 0;
    color: var(--color-danger-text);
    font-family: var(--font-family-sans);
    font-size: 14px;
  }

  @media (max-width: 768px) {
    &__layout {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    &__sidebar {
      flex-direction: row;
      align-items: stretch;
      justify-content: space-between;

      :deep(.library-filters) {
        order: 1;
      }

      :deep(.sort-select) {
        order: 2;
      }
    }
  }
}
</style>
